import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Star, TrendingDown } from "lucide-react";

const TIER_STYLE = {
  star: { badge: "bg-amber-500 text-white", label: "Star" },
  solid: { badge: "bg-sky-500 text-white", label: "Solid" },
  average: { badge: "bg-slate-300 text-slate-700", label: "Avg" },
  underperformer: { badge: "bg-red-500 text-white", label: "Low" },
};

export default function OfferPerformancePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("top"); // top | under

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/admin/offer-performance`); setData(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">No data</p>;

  const list = view === "top" ? data.top_performers : data.underperformers;

  return (
    <div className="space-y-4" data-testid="offer-performance-panel">
      <div className="flex gap-2" data-testid="perf-tabs">
        <button onClick={() => setView("top")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${view === "top" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="perf-tab-top">
          Top 10
        </button>
        <button onClick={() => setView("under")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${view === "under" ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="perf-tab-under">
          Underperformers
        </button>
        <span className="ml-auto text-xs text-slate-400 self-center">{data.total_ranked} ranked</span>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No offers in this tier yet</p>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="perf-list">
          {list.map((r, i) => {
            const style = TIER_STYLE[r.tier] || TIER_STYLE.average;
            return (
              <div key={r.business_id} className="flex items-center gap-3 px-3 py-2.5 bg-white border border-slate-100 rounded-xl">
                <span className="text-xs font-bold text-slate-300 w-5 text-center">{view === "top" ? i + 1 : "—"}</span>
                {r.logo_url ? (
                  <BrandLogo name={r.name} logoUrl={r.logo_url} size="sm" />
                ) : (
                  <BrandLogo name={r.name} size="sm" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{r.name}</p>
                  <p className="text-[10px] text-slate-400">
                    {r.views} views &middot; {r.favorites} favs &middot; {r.claims} claims &middot; {r.conversion_pct}% conv
                  </p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${style.badge}`}>{style.label}</span>
                <span className="text-sm font-bold text-slate-700">{r.performance_score}</span>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-slate-400 text-center">Score = 35% conversion + 25% favorites + 20% views + 20% stickiness</p>
    </div>
  );
}
